/**
 * ProductShowcase.jsx — fetches image URLs from API instead of Firebase SDK
 */

import { useState, useEffect, useRef } from 'react'
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import ProductScene from './ProductScene'
import { useCanvasPause } from '../Welcome/useCanvasPause'
import './ProductShowcase.css'

const API = process.env.REACT_APP_API_URL || "";



const PRODUCTS = [
  {
    id: 'base', key: 'gcs-base',
    name: 'DroneCommanderGCS', variant: 'ALL-IN-ONE',
    tagline: 'Complete mission control in your hands.',
    accent: '#5d179e', accentRgb: '93,23,158',
    features: [
      { icon: '◈', label: 'Plan autonomous missions', desc: 'Drop waypoints, set altitude, speed and actions per point.' },
      { icon: '◉', label: 'Monitor live telemetry', desc: 'Attitude, GPS, battery and link quality at 10 Hz.' },
      { icon: '◎', label: 'Adjust parameters mid-flight', desc: 'Tune PID, flight modes and limits without landing.' },
      { icon: '◍', label: 'Perform safety actions', desc: 'RTL, land, hold and emergency stop at one tap.' },
    ],
  },
  {
    id: 'agri', key: 'gcs-agri',
    name: 'DroneCommanderGCS', variant: 'AGRI',
    tagline: 'Precision agriculture from above.',
    accent: '#2d6a4f', accentRgb: '45,106,79',
    features: [
      { icon: '◈', label: 'Grid spray mission planning', desc: 'Auto-generate spray paths for any field polygon.' },
      { icon: '◉', label: 'NDVI crop health overlay', desc: 'Live crop health heatmap during flight.' },
      { icon: '◎', label: 'Variable rate application', desc: 'Adjust spray volume zone-by-zone based on field data.' },
      { icon: '◍', label: 'Field boundary mapping', desc: 'Walk or fly to define field boundaries precisely.' },
    ],
  },
  {
    id: 'survey', key: 'gcs-survey',
    name: 'DroneCommanderGCS', variant: 'SURVEY',
    tagline: 'Map the world with centimeter accuracy.',
    accent: '#4a6fa5', accentRgb: '74,111,165',
    features: [
      { icon: '◈', label: 'Photogrammetry grid flights', desc: 'Auto-plan overlap passes for point cloud generation.' },
      { icon: '◉', label: 'GCP marker management', desc: 'Mark and manage ground control points on-site.' },
      { icon: '◎', label: 'Elevation contour preview', desc: 'Live terrain profile as mission is flown.' },
      { icon: '◍', label: 'KML / GeoJSON export', desc: 'Export flight logs directly for GIS processing.' },
    ],
  },
  {
    id: 'surveillance', key: 'gcs-surveillance',
    name: 'DroneCommanderGCS', variant: 'CAMERA',
    tagline: 'Capture the world from above.',
    accent: '#6b7280', accentRgb: '107,114,128',
    features: [
      {
        icon: '◈',
        label: '4K Photo & Video Capture',
        desc: 'Capture high-resolution images and smooth 4K video with full camera control.',
      },
      {
        icon: '◉',
        label: 'Live Camera Preview',
        desc: 'Real-time FPV feed with zoom, focus, and exposure adjustments.',
      },
      {
        icon: '◎',
        label: 'Smart Capture Modes',
        desc: 'Waypoint shots, orbit capture, and automated cinematic movements.',
      },
      {
        icon: '◍',
        label: 'Media Storage & Export',
        desc: 'Instant save, preview, and export photos/videos for editing or sharing.',
      },
    ],
  },
]

export default function ProductShowcase() {
  const [active, setActive] = useState(0)
  const [animKey, setAnimKey] = useState(0)
  const [imgUrls, setImgUrls] = useState({})
  const { sectionRef, frameloop } = useCanvasPause('200px')

  const wrapRef = useRef(null)
  const headlineRef = useRef(null)
  const subRef = useRef(null)
  const badgeRef = useRef(null)
  const tabsRef = useRef(null)
  const leftRef = useRef(null)
  const rightRef = useRef(null)
  const ctaRef = useRef(null)

  // ── Fetch all product images ──────────────────────────────────────────────
  useEffect(() => {
    fetch(`${API}/api/media/products`)
      .then(res => {
        if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
        return res.json();
      })
      .then(map => setImgUrls(map))
      .catch((err) => console.error("Failed to fetch media:", err))
  }, [])

  // ── Cinematic scroll reveals ──────────────────────────────────────────────
  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo(headlineRef.current,
        { opacity: 0, y: 80, rotateX: 25, transformPerspective: 800 },
        {
          opacity: 1, y: 0, rotateX: 0, duration: 1.2, ease: 'expo.out',
          scrollTrigger: { trigger: headlineRef.current, start: 'top 85%', toggleActions: 'restart none none reverse', invalidateOnRefresh: true }
        }
      )
      gsap.fromTo([subRef.current, badgeRef.current],
        { opacity: 0, y: 40 },
        {
          opacity: 1, y: 0, duration: 0.9, ease: 'expo.out', stagger: 0.12,
          scrollTrigger: { trigger: subRef.current, start: 'top 88%', toggleActions: 'restart none none reverse' }
        }
      )
      gsap.fromTo(tabsRef.current,
        { opacity: 0, x: -40 },
        {
          opacity: 1, x: 0, duration: 0.8, ease: 'expo.out',
          scrollTrigger: { trigger: tabsRef.current, start: 'top 88%', toggleActions: 'restart none none reverse' }
        }
      )
      gsap.fromTo(leftRef.current,
        { opacity: 0, x: -60, rotateY: -8, transformPerspective: 1000 },
        {
          opacity: 1, x: 0, rotateY: 0, duration: 1.1, ease: 'expo.out',
          scrollTrigger: { trigger: leftRef.current, start: 'top 82%', toggleActions: 'restart none none reverse' }
        }
      )
      gsap.fromTo(rightRef.current,
        { opacity: 0, x: 60, rotateY: 8, transformPerspective: 1000 },
        {
          opacity: 1, x: 0, rotateY: 0, duration: 1.1, ease: 'expo.out', delay: 0.1,
          scrollTrigger: { trigger: rightRef.current, start: 'top 82%', toggleActions: 'restart none none reverse' }
        }
      )
      gsap.fromTo(ctaRef.current,
        { opacity: 0, y: 30 },
        {
          opacity: 1, y: 0, duration: 0.8, ease: 'expo.out',
          scrollTrigger: { trigger: ctaRef.current, start: 'top 90%', toggleActions: 'restart none none reverse' }
        }
      )
    }, wrapRef)
    return () => ctx.revert()
  }, [])

  const handleTab = (i) => {
    if (i === active) return
    gsap.to([leftRef.current, rightRef.current], {
      opacity: 0, y: 20, duration: 0.2, ease: 'power2.in',
      onComplete: () => {
        setActive(i)
        setAnimKey(k => k + 1)
        gsap.fromTo([leftRef.current, rightRef.current],
          { opacity: 0, y: 20 },
          { opacity: 1, y: 0, duration: 0.4, ease: 'expo.out', stagger: 0.06 }
        )
      }
    })
  }

  const p = PRODUCTS[active]
  const imgState = imgUrls[p.key]

  return (
    <section
      ref={(el) => { wrapRef.current = el; sectionRef.current = el }}
      id="products"
      className="ps-wrap"
    >
      <div className="ps-grid-bg" />
      <div className="ps-container">

        {/* Header */}
        <div className="ps-header">
          <div className="ps-eyebrow">
            <span className="ps-dot" style={{ background: '#5d179e' }} />
            OUR PRODUCTS
          </div>
          <h2 ref={headlineRef} className="ps-h2">
            One Platform.<br />
            <span className="ps-h2-em">— Four Missions.</span>
          </h2>
          <p ref={subRef} className="ps-sub">
            DroneCommanderGCS is a native Android ground control platform built for
            serious drone operations. Four specialised variants. One unified codebase.
          </p>
          <div ref={badgeRef} className="ps-badge">
            <span className="ps-badge-dot" />
            LAUNCHING SOON ON ANDROID
          </div>
        </div>

        {/* Tabs + Mobile Carousel */}
        <div ref={tabsRef} className="ps-tabs-wrapper">

          {/* Desktop tab strip */}
          <div className="ps-tabs">
            {PRODUCTS.map((prod, i) => (
              <button key={prod.id}
                className={`ps-tab ${i === active ? 'ps-tab-active' : ''}`}
                style={{ '--a': prod.accent }}
                onClick={() => handleTab(i)}
              >
                {prod.variant}
              </button>
            ))}
          </div>

          {/* Mobile carousel arrows + label */}
          <button
            className="ps-tab-arrow ps-tab-arrow--left"
            onClick={() => handleTab(active - 1)}
            disabled={active === 0}
            aria-label="Previous product"
          >
            ‹
          </button>
          <span
            className="ps-mobile-tab-label"
            style={{ color: p.accent, borderColor: p.accent }}
          >
            {p.variant}
          </span>
          <button
            className="ps-tab-arrow ps-tab-arrow--right"
            onClick={() => handleTab(active + 1)}
            disabled={active === PRODUCTS.length - 1}
            aria-label="Next product"
          >
            ›
          </button>

        </div>

        {/* Split */}
        <div className="ps-split">

          {/* Left */}
          <div ref={leftRef} className="ps-left" key={`left-${animKey}`}>
            <div className="ps-pname">
              <span className="ps-pname-main">{p.name}</span>
              <span className="ps-pname-tag" style={{ color: p.accent, borderColor: p.accent }}>
                {p.variant}
              </span>
            </div>
            <p className="ps-tagline" style={{ borderLeftColor: p.accent }}>{p.tagline}</p>
            <ul className="ps-features">
              {p.features.map((f, i) => (
                <li key={i} className="ps-feat" style={{ '--delay': `${i * 0.07}s` }}>
                  <span className="ps-feat-icon" style={{ color: p.accent }}>{f.icon}</span>
                  <span className="ps-feat-body">
                    <span className="ps-feat-label">{f.label}</span>
                    <span className="ps-feat-desc">{f.desc}</span>
                  </span>
                </li>
              ))}
            </ul>
            <div className="ps-soon" style={{ color: p.accent }}>
              <span className="ps-soon-dot" style={{ background: p.accent }} />
              Coming Soon on Android
            </div>
          </div>

          {/* Right — 3D scene */}
          <div ref={rightRef} className="ps-right">
            <div className="ps-frame-wrap" style={{ '--argb': p.accentRgb }}>
              <div className="ps-scene-bg">
                <ProductScene variantId={p.id} accentHex={p.accent} frameloop={frameloop} />
              </div>
              <div className="ps-frame" key={`frame-${animKey}`}>
                <div className="ps-frame-bar">
                  <span className="ps-circle" /><span className="ps-circle" /><span className="ps-circle" />
                  <span className="ps-frame-title">{p.name} · {p.variant}</span>
                </div>
                <div className="ps-screen">
                  {!imgState && (
                    <div className="ps-loading">
                      <div className="ps-loading-ring" style={{ borderTopColor: p.accent }} />
                      <span>LOADING</span>
                    </div>
                  )}
                  {imgState && (
                    <img src={imgState} alt={`${p.name} ${p.variant}`} className="ps-img" />
                  )}
                </div>
              </div>
              <span className="ps-corner ps-tl" style={{ borderColor: p.accent }} />
              <span className="ps-corner ps-tr" style={{ borderColor: p.accent }} />
              <span className="ps-corner ps-bl" style={{ borderColor: p.accent }} />
              <span className="ps-corner ps-br" style={{ borderColor: p.accent }} />
            </div>
          </div>

        </div>

        {/* CTA */}
        <div ref={ctaRef} className="ps-cta-row">
          <div className="ps-line" />
          <a href="/our-businesses/overview" className="ps-cta">
            Explore All Products <span className="ps-arrow">→</span>
          </a>
          <div className="ps-line" />
        </div>

      </div>
    </section>
  )
}